import { httpsCallable } from 'firebase/functions';
import { functions } from '../platform/firebase/clients';
import { BUILD_GIT_SHA, BUILD_ID } from './buildInfo';
import { shouldDowngradeToWarning } from '../utils/errorCategory';
import { parseReportLocation, ReportLocation } from '../utils/stackTrace';

export type LogSeverity = 'DEBUG' | 'INFO' | 'NOTICE' | 'WARNING' | 'ERROR' | 'CRITICAL';

interface ReportedHttpRequest {
  method: string;
  url?: string;
  userAgent?: string;
}

interface ReportedUser {
  uid?: string;
  email?: string | null;
}

interface ReportedServiceContext {
  service: string;
  version: string;
}

export interface LogEventRequest {
  severity: LogSeverity;
  message: string;
  context?: Record<string, unknown>;
  // Optional fields used when severity is ERROR/CRITICAL — populated by
  // `logError`. The callable falls back to defaults when these are absent,
  // so plain `logEvent` calls still work unchanged.
  stack?: string;
  reportLocation?: ReportLocation;
  httpRequest?: ReportedHttpRequest;
  user?: ReportedUser;
  serviceContext?: ReportedServiceContext;
}

const logFn = httpsCallable<LogEventRequest, { ok: true }>(functions, 'logFrontendEvent');

const SERVICE_NAME = 'workbench-frontend';
const SERVICE_VERSION = BUILD_GIT_SHA || BUILD_ID || 'unknown';

function currentHttpRequest(): ReportedHttpRequest | undefined {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return undefined;
  }
  return {
    method: 'GET',
    url: window.location.href,
    userAgent: navigator.userAgent,
  };
}

function currentServiceContext(): ReportedServiceContext {
  return { service: SERVICE_NAME, version: SERVICE_VERSION };
}

export async function logEventAsync(
  severity: LogSeverity,
  message: string,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    await logFn({ severity, message, context });
  } catch (err) {
    console.warn('logEvent failed:', err);
  }
}

/**
 * Send a structured event to Cloud Logging (log name `frontend`). Fire-and-
 * forget — never throws, never blocks UI. Only fires when a user is signed in;
 * the callable requires auth, so unauthenticated events are silently dropped.
 *
 * For severity ≥ ERROR prefer `logError` so the entry shows up in Cloud
 * Error Reporting (grouped) rather than only in Logs Explorer.
 *
 * The callable runs against the Firestore emulator in local dev; logs will go
 * to the local emulator's stdout rather than real Cloud Logging.
 */
export function logEvent(
  severity: LogSeverity,
  message: string,
  context?: Record<string, unknown>,
): void {
  void logEventAsync(severity, message, context);
}

export interface LogErrorOptions {
  /** Short human label describing where the error originated (e.g. "TaskQueueViewModel.subscribe"). */
  context?: string;
  /** Arbitrary extra fields preserved as `jsonPayload.frontendContext.*`. */
  metadata?: Record<string, unknown>;
  /** Override the auto-detected user (e.g. when called outside an auth context). */
  user?: ReportedUser;
  /** Override the default severity. Useful for forcing CRITICAL. */
  severity?: LogSeverity;
}

function buildErrorMessage(error: unknown, contextLabel?: string): string {
  const base =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : (() => {
            try {
              return String(error);
            } catch {
              return 'Unknown error';
            }
          })();
  return contextLabel ? `[${contextLabel}] ${base}` : base;
}

function extractStack(error: unknown): string | undefined {
  if (error instanceof Error && typeof error.stack === 'string') return error.stack;
  if (error && typeof (error as { stack?: unknown }).stack === 'string') {
    return (error as { stack: string }).stack;
  }
  return undefined;
}

/**
 * Send an error to Cloud Logging with the ReportedErrorEvent shape so it
 * shows up in Cloud Error Reporting. Auto-downgrades expected
 * auth/network errors to WARNING (so they don't pollute the error groups
 * dashboard) — pass `severity: 'ERROR'` explicitly to override.
 */
export async function logErrorAsync(
  error: unknown,
  options: LogErrorOptions = {},
): Promise<void> {
  const stack = extractStack(error);
  const reportLocation = parseReportLocation(stack);
  const message = buildErrorMessage(error, options.context);
  const severity =
    options.severity || (shouldDowngradeToWarning(error) ? 'WARNING' : 'ERROR');

  // WARNING entries don't need the full ReportedErrorEvent shape — Error
  // Reporting only ingests ERROR/CRITICAL — but the stack and report
  // location are still useful for debugging in Logs Explorer, so we send
  // them for both severities.
  try {
    await logFn({
      severity,
      message,
      context: options.metadata,
      stack,
      reportLocation,
      httpRequest: currentHttpRequest(),
      user: options.user,
      serviceContext: currentServiceContext(),
    });
  } catch (err) {
    console.warn('logError failed:', err);
  }
}

export function logError(error: unknown, options: LogErrorOptions = {}): void {
  void logErrorAsync(error, options);
}
